import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router-dom';
import RouterPropTypes from 'react-router-prop-types';

import useProductSchema from '../../../../hooks/schema/useProductSchema';
import Product from '../../../../models/Product';
import { modelOf } from '../../../../prop-types';
import { LocationContextPropType } from '../../../../services/LocationContext';
import RouteService from '../../../../services/RouteService';
import AccountStore from '../../../../store/AccountStore';
import CategoryStore from '../../../../store/CategoryStore';
import ConfigStore from '../../../../store/ConfigStore';
import CountryStore from '../../../../store/CountryStore';
import CurrencyStore from '../../../../store/CurrencyStore';
import ManufacturerStore from '../../../../store/ManufacturerStore';
import UIStore from '../../../../store/UIStore';
import CommonPage from '../../../../types/CommonPage';
import ProductClass from '../../../../types/ProductClass';
import SchemaType from '../../../../types/seo/SchemaType';
import { parse } from '../../../../util/queryString';
import {
  appendCategoryManufacturerPathSuffix,
  getCategoryManufacturerRouteState,
} from '../../../../util/categoryManufacturerRouting';
import SEOSchema from '../../../head/SEOSchema';
import { buildCategoryDisplayName } from '../../../../pages/CategoryPage/categorySeoTitle';

export const ProductListSchema = ({
  accountStore,
  categoryStore,
  configStore,
  countryStore,
  currencyStore,
  manufacturerStore,
  uiStore,
  routeService,
  locationContext,
  match,
  location,
  products,
}) => {
  const { getProductSchema, getProductGroupSchema } = useProductSchema({
    accountStore,
    categoryStore,
    configStore,
    countryStore,
    currencyStore,
    routeService,
    locationContext,
  });

  const manufacturerParam = match?.params?.manufacturer;
  const parsedSearch = parse(location?.search || '');
  const rawManufacturerQueryValues = parsedSearch?.f?.manufacturers;
  const queryManufacturerValues = Array.isArray(rawManufacturerQueryValues)
    ? rawManufacturerQueryValues
    : rawManufacturerQueryValues
    ? [rawManufacturerQueryValues]
    : [];
  const categoryRouteState = getCategoryManufacturerRouteState({
    manufacturerStore,
    manufacturerParam,
    queryManufacturerValues,
  });
  const effectiveManufacturerParam =
    categoryRouteState.effectiveManufacturerParam;
  const title =
    categoryStore?.activeCategory?.SEO_title ||
    categoryStore?.activeCategory?.name ||
    '';
  const isCategoryPage = uiStore.currentPage === CommonPage.CATEGORY;
  let name = isCategoryPage
    ? buildCategoryDisplayName({
        activeCategory: categoryStore?.activeCategory,
        manufacturerStore,
        manufacturerParam: effectiveManufacturerParam,
      })
    : configStore.store.name + (title.length > 0 ? ` - ${title}` : '');
  const description = categoryStore?.activeCategory?.SEO_description;
  const baseUrl = `${locationContext.protocol}//${locationContext.host}`;
  const categoryPath =
    isCategoryPage && categoryStore?.activeCategory
      ? appendCategoryManufacturerPathSuffix(
          routeService.getCategoryPath(categoryStore.activeCategory),
          effectiveManufacturerParam
        )
      : '';
  const url = isCategoryPage
    ? `${baseUrl}${categoryPath}`
    : `${baseUrl}${location?.pathname || ''}${location?.search || ''}`;

  const itemListElement = products?.map((product) => {
    return product.class === ProductClass.MULTI
      ? getProductGroupSchema(product)
      : getProductSchema(product);
  });

  // Frontpage has the WebPage type
  const data =
    uiStore.currentPage === CommonPage.HOME
      ? {
          '@context': 'https://schema.org',
          '@type': SchemaType.ITEM_LIST,
          itemListElement: itemListElement,
          numberOfItems: products?.length || 0,
        }
      : {
          '@context': 'https://schema.org',
          '@type': SchemaType.COLLECTION_PAGE,
          name: name,
          url,
          ...(description && { description }),
          mainEntity: {
            '@type': SchemaType.ITEM_LIST,
            itemListElement: itemListElement,
            numberOfItems: products?.length || 0,
          },
        };

  return <SEOSchema data={data} />;
};

ProductListSchema.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  categoryStore: modelOf(CategoryStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  countryStore: modelOf(CountryStore).isRequired,
  currencyStore: modelOf(CurrencyStore).isRequired,
  manufacturerStore: modelOf(ManufacturerStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  locationContext: LocationContextPropType.isRequired,
  match: RouterPropTypes.match,
  location: RouterPropTypes.location,
  products: PropTypes.arrayOf(modelOf(Product)),
};

export default withRouter(
  inject(
    'accountStore',
    'categoryStore',
    'configStore',
    'countryStore',
    'currencyStore',
    'manufacturerStore',
    'uiStore',
    'routeService',
    'locationContext'
  )(observer(ProductListSchema))
);
